import React, { useEffect, useState } from "react";
import { ReactComponent as BarChart } from "../../../../../icons/baricon.svg";
import { ReactComponent as Info } from "../../../../../icons/info-Simulator.svg";

interface CostItem {
  label: string;
  value: string;
  percentage?: string;
}

interface CostSection {
  gna_value: number;
  no_of_tgna_applications: number;
  tgna_value: number;
  unused_gna_mwh: number;
}

interface DisplaySection {
  title: string;
  titleColor: string;
  iconColor: string;
  iconBgColor: string;
  items: CostItem[];
}

// Skeleton Loader Component
const SkeletonLoader = () => {
  return (
    <div className="flex gap-6 w-full">
      {[1, 2, 3, 4].map((section) => (
        <div
          key={section}
          className="bg-white border border-[#DFEAF2] rounded-2xl shadow-lg p-4 w-full h-full animate-pulse"
        >
          {/* Title Skeleton */}
          <div className="h-6 bg-gray-200 rounded w-32 mb-3"></div>

          {/* Items Skeleton */}
          <div className="space-y-3">
            {[1, 2, 3, 4].map((item) => (
              <div
                key={item}
                className="flex items-start gap-4 p-4 mb-2 bg-white border border-[#DFEAF2] shadow-sm rounded-xl"
              >
                {/* Icon Skeleton */}
                <div className="bg-gray-200 rounded-full w-9 h-9 flex-shrink-0"></div>

                {/* Content Skeleton */}
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="h-4 bg-gray-200 rounded w-40"></div>
                    <div className="w-4 h-4 bg-gray-200 rounded"></div>
                  </div>
                  <div className="h-6 bg-gray-200 rounded w-24"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default function GnaStatisticsVolumeAnalysis({
  selectedBlock,
  refreshKey,
  setError,
  loading,
  data,
}: {
  selectedBlock: any[];
  refreshKey: number;
  setError?: any;
  loading: boolean;
  data: CostSection[];
}) {
  const [costData, setCostData] = useState<DisplaySection[]>([]);

  // Format number with commas and 2 decimal places
  const formatNumber = (num: number): string => {
    return num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  useEffect(() => {
    if (data && data.length >= 2) {
      const current = data[0];
      const optimised = data[1];
      const Simulated = data[2];
      const difference = data[3];

      const formattedData: DisplaySection[] = [
        {
          title: "Present",
          titleColor: "text-[#819ACC]",
          iconColor: "text-blue-500",
          iconBgColor: "bg-blue-100",
          items: [
            {
              label: "GNA Vol (MWh)",
              value: formatNumber(current.gna_value),
            },
            {
              label: "TGNA Vol (MWh)",
              value: formatNumber(current.tgna_value),
            },
            {
              label: "Unused GNA Vol (MWh)",
              value: formatNumber(current.unused_gna_mwh),
            },
            // {
            //   label: "No. of TGNA Application",
            //   value: formatNumber(current.no_of_tgna_applications),
            // },
          ],
        },
        {
          title: "Optimal",
          titleColor: "text-[#34656D]",
          iconColor: "text-teal-500",
          iconBgColor: "bg-teal-100",
          items: [
            {
              label: "GNA Vol (MWh)",
              value: formatNumber(optimised.gna_value),
            },
            {
              label: "TGNA Vol (MWh)",
              value: formatNumber(optimised.tgna_value),
            },
            {
              label: "Unused GNA Vol (MWh)",
              value: formatNumber(optimised.unused_gna_mwh),
            },
            // {
            //   label: "No. of TGNA Application",
            //   value: formatNumber(optimised.no_of_tgna_applications),
            // },
          ],
        },
        {
          title: "Simulated",
          titleColor: "text-[#F1935C]",
          iconColor: "text-orange-500",
          iconBgColor: "bg-orange-100",
          items: [
            {
              label: "GNA Vol (MWh)",
              value: formatNumber(Simulated.gna_value),
            },
            {
              label: "TGNA Vol (MWh)",
              value: formatNumber(Simulated.tgna_value),
            },
            {
              label: "Unused GNA Vol (MWh)",
              value: formatNumber(Simulated.unused_gna_mwh),
            },
            // {
            //   label: "No. of TGNA Application",
            //   value: formatNumber(Simulated.no_of_tgna_applications),
            // },
          ],
        },
        {
          title: "Difference (Optimal vs Present)",
          titleColor: "text-[#FF1028]",
          iconColor: "text-orange-500",
          iconBgColor: "bg-red-100",
          items: [
            {
              label: "GNA Vol (MWh)",
              value: formatNumber(difference.gna_value),
            },
            {
              label: "TGNA Vol (MWh)",
              value: formatNumber(difference.tgna_value),
            },
            {
              label: "Unused GNA Vol (MWh)",
              value: formatNumber(difference.unused_gna_mwh),
            },
            // {
            //   label: "No. of TGNA Application",
            //   value: formatNumber(difference.no_of_tgna_applications),
            // },
          ],
        },
      ];

      setCostData(formattedData);
    }
  }, [data, refreshKey]);

  return (
    <>
      {loading ? (
        <div className="w-full !overflow-auto !h-[calc(100vh-560px)]">
          <div className="w-full pb-2">
            <div className="flex flex-nowrap justify-around gap-6 w-full">
              <SkeletonLoader />
            </div>
          </div>
        </div>
      ) : (
        <div className="w-full !overflow-auto !h-[calc(100vh-560px)]">
          <div className="w-full pb-2">
            <div className="flex flex-nowrap justify-around gap-6 w-full">
              {costData.length > 0 ? (
                <div className="flex gap-6 w-full">
                  {costData.map((section, sectionIndex) => (
                    <div
                      key={sectionIndex}
                      className="bg-white border border-[#DFEAF2] rounded-2xl shadow-lg p-4 w-full h-full"
                    >
                      {/* Section Title */}
                      <h2
                        className={`text-base font-medium mb-3 ${section.titleColor}`}
                      >
                        {section.title}
                      </h2>

                      {/* Cost Items */}
                      <div className="space-y-3">
                        {section.items.map((item, itemIndex) => (
                          <div
                            key={itemIndex}
                            className="flex items-start gap-4 p-4 mb-2 bg-white border border-[#DFEAF2] shadow-sm rounded-xl"
                          >
                            {/* Icon */}
                            <div
                              className={`${section.iconBgColor} rounded-full p-2 flex-shrink-0`}
                            >
                              <BarChart
                                className={`w-5 h-5 ${section.iconColor}`}
                              />
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm text-[#819ACC] font-medium">
                                  {item.label}
                                </span>
                                <Info className="w-4 h-4 text-slate-300" />
                              </div>
                              <div className="flex items-baseline gap-2">
                                <span className="text-lg font-medium text-[#525062]">
                                  {item.value}
                                </span>
                                {item.percentage && (
                                  <span className="text-lg font-semibold text-[#2B3674]">
                                    {item.percentage}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8 w-full">
                  No Data Available...
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}